import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';
import { useCurrency } from '../currency/CurrencyProvider';
import { useDashboard } from '../api/queries';
import { useDashboardFinanciero } from '../api/queries-finanzas';
import { usePos } from '../pos/PosProvider';
import { useSync } from '../sync/SyncProvider';
import { AreaChart } from '../components/AreaChart';
import { Donut } from '../components/Donut';
import { DataNotice } from '../components/Badges';
import { TransactionsTable } from '../components/TransactionsTable';
import {
  ArrowRightIcon,
  CalendarIcon,
  PlusIcon,
  RouteIcon,
  TrendUpIcon,
} from '../components/icons';
import {
  formatCompactRaw,
  formatDateLong,
  formatDateShort,
  formatDecimal,
  formatInteger,
  formatMoney as formatBs,
  greeting,
} from '../lib/format';

export function DashboardPage() {
  const { user, demo } = useAuth();
  const { formatMoney, formatCompact, usdRate } = useCurrency();
  const bsOr = (ves: number | undefined | null, usd: number | undefined | null) =>
    ves === undefined || ves === null ? (usd ?? 0) * usdRate : ves;
  const { openPos } = usePos();
  const { online, pending } = useSync();
  const navigate = useNavigate();
  const { data, isLoading, isError, refetch, isFetching } = useDashboard();
  const financiero = useDashboardFinanciero();
  const fin = financiero.data?.kpis;

  const kpis = data?.kpis;
  const totalClientes = kpis?.clientesActivos ?? 0;
  const comprando = kpis?.clientesComprando ?? 0;
  const activacion = totalClientes > 0 ? Math.round((comprando / totalClientes) * 100) : 0;
  const firstName = user?.nombre.split(' ')[0] ?? 'equipo';

  const chartPoints =
    data?.serie.map((point) => ({
      label: formatDateShort(point.fecha),
      value: point.cajas,
      detail: `${formatInteger(point.cajas)} unidades · ${point.pedidos} pedidos`,
    })) ?? [];

  return (
    <div className="dashboard">
      <header className="page-head">
        <div>
          <p className="eyebrow">Operaciones comerciales</p>
          <h1 className="page-title">Panel comercial</h1>
          <p className="page-sub">
            El pulso de la venta en ruta, la cobranza y el cumplimiento de GRAVENCA.
          </p>
        </div>
        <div className="page-head__meta">
          <span className={`live-pill${online ? '' : ' live-pill--offline'}`}>
            <span className="live-pill__dot" aria-hidden="true" />
            {online ? 'En vivo' : 'Sin conexión'}
          </span>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => void refetch()}
            disabled={isFetching}
          >
            {isFetching ? 'Actualizando…' : 'Actualizar'}
          </button>
        </div>
      </header>

      <DataNotice
        demo={data?.demo}
        offline={!online && !data?.demo}
        message={
          data?.demo
            ? 'Panel en modo demostración · los pedidos que crees quedarán en la cola local.'
            : !online
              ? 'Sin conexión: mostrando la última foto guardada en el dispositivo.'
              : undefined
        }
      />

      {isError ? (
        <div className="inline-error" role="alert">
          <span>No se pudo consultar el resumen comercial.</span>
          <button type="button" className="text-link" onClick={() => void refetch()}>
            Reintentar
          </button>
        </div>
      ) : null}

      <div className="kpi-grid">
        <article className="card hero-card">
          <div className="hero-card__top">
            <span className="date-pill">
              <CalendarIcon size={15} />
              Hoy, {formatDateLong()}
            </span>
            <span className={`hero-status${online ? '' : ' hero-status--offline'}`}>
              <span className="hero-status__dot" aria-hidden="true" />
              {!online
                ? 'Sin conexión'
                : data?.demo
                  ? 'Modo demo'
                  : 'Operación en vivo'}
            </span>
          </div>

          <h2 className="hero-card__greeting">
            {greeting()}, <span>{firstName}</span>
          </h2>
          <p className="hero-card__sub">
            Cada unidad cuenta. Así va la operación comercial de GRAVENCA el día de hoy.
          </p>

          <div className="hero-card__metric">
            <strong>{isLoading ? '—' : formatInteger(kpis?.cajasHoy ?? 0)}</strong>
            <span>unidades despachadas hoy</span>
          </div>

          <div className="pill-row">
            <span className="pill pill--soft">
              <TrendUpIcon size={13} />
              {formatInteger(kpis?.pedidosHoy ?? 0)} pedidos
            </span>
            <span className="pill pill--soft">
              {formatInteger(kpis?.cajasHoy ?? 0)} unidades hoy
            </span>
            {pending > 0 ? (
              <span className="pill pill--warning">{pending} en cola local</span>
            ) : (
              <span className="pill pill--soft">Meta {formatInteger(kpis?.metaCajas ?? 0)} unidades</span>
            )}
            {isLoading ? (
              <span className="pill pill--soft">
                <span className="mini-spinner" aria-hidden="true" />
                calculando
              </span>
            ) : null}
          </div>

          <div className="hero-card__chart">
            {isLoading ? (
              <div className="chart-skeleton" aria-label="Cargando gráfico" />
            ) : (
              <AreaChart points={chartPoints} seriesName="Unidades" />
            )}
          </div>

          <div className="hero-card__actions">
            <button
              type="button"
              className="btn btn--outline"
              onClick={() => navigate('/pedidos')}
            >
              <RouteIcon size={17} />
              Iniciar ruta comercial
            </button>
            <button type="button" className="btn btn--primary" onClick={openPos}>
              <PlusIcon size={17} />
              Nuevo Pedido / POS
            </button>
          </div>
        </article>

        <div className="kpi-stack">
          <article className="card stat-card">
            <div className="stat-card__head">
              <span className="stat-card__label">Días Calle Promedio</span>
              <span className="stat-card__icon">
                <RouteIcon size={17} />
              </span>
            </div>
            <div className="stat-card__value">
              {isLoading ? '—' : formatDecimal(kpis?.diasCallePromedio ?? 0, 1)}
            </div>
            <p className="stat-card__hint">Días efectivos de visita por vendedor, últimos 30 días.</p>
            <button
              type="button"
              className="stat-card__link"
              onClick={() => navigate('/pedidos')}
            >
              Ver desglose <ArrowRightIcon size={14} />
            </button>
          </article>

          <article className="card stat-card">
            <div className="stat-card__head">
              <span className="stat-card__label">Cobranza del Día</span>
              <span className="stat-card__icon stat-card__icon--accent">
                <ArrowRightIcon size={17} />
              </span>
            </div>
            <div className="stat-card__value stat-card__value--accent">
              {isLoading ? '—' : formatBs(bsOr(kpis?.cobranzaHoyVes, kpis?.cobranzaHoyUsd), 'VES')}
            </div>
            <p className="stat-card__hint">Recibos confirmados hoy en caja y banco.</p>
            <button
              type="button"
              className="stat-card__link"
              onClick={() => navigate('/cobranzas')}
            >
              Ver cobranzas <ArrowRightIcon size={14} />
            </button>
          </article>

          <article className="card stat-card stat-card--donut">
            <span className="stat-card__label">Cumplimiento de Meta</span>
            <Donut value={kpis?.cumplimientoMeta ?? 0} label="Cumplimiento de meta" />
            <p className="stat-card__hint">
              Pedidos {formatCompactRaw(bsOr(kpis?.ventasMesVes, kpis?.ventasMesUsd))} / {formatCompact(kpis?.metaUsd ?? 0)}
            </p>
            <span className="stat-card__caption">Mes en curso · pedidos</span>
          </article>

          <article className="card stat-card">
            <span className="stat-card__label">Clientes con compra</span>
            <div className="stat-card__value">{isLoading ? '—' : `${activacion}%`}</div>
            <div
              className="progress"
              role="progressbar"
              aria-valuenow={activacion}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Clientes activados"
            >
              <span style={{ width: `${activacion}%` }} />
            </div>
            <p className="stat-card__hint">
              {formatInteger(comprando)} de {formatInteger(totalClientes)} con pedidos en 30 días
            </p>
            <span className="stat-card__caption">Cartera activa · 30 días</span>
          </article>
        </div>
      </div>

      {!demo && fin ? (
        <section className="financial-section" aria-labelledby="financial-title">
          <div className="section-head">
            <div>
              <p className="eyebrow">Contabilidad en vivo</p>
              <h2 id="financial-title">Salud financiera</h2>
            </div>
            <Link to="/contabilidad" className="text-link">Ver balance completo →</Link>
          </div>
          <div className="summary-strip summary-strip--6">
            <Link to="/facturas" className="summary-tile summary-tile--link">
              <span>Facturado del mes</span>
              <strong>{formatCompactRaw(bsOr(fin.facturadoMesVes, fin.facturadoMesUsd))}</strong>
              <small>{fin.facturasPendientes} facturas pendientes</small>
            </Link>
            <Link to="/cobranzas" className="summary-tile summary-tile--link">
              <span>Cuentas por cobrar</span>
              <strong>{formatCompactRaw(bsOr(fin.cxcVes, fin.cxcUsd))}</strong>
              <small>Cobrado {formatCompactRaw(bsOr(fin.cobranzaMesVes, fin.cobranzaMesUsd))} este mes</small>
            </Link>
            <Link to="/compras" className="summary-tile summary-tile--link">
              <span>Cuentas por pagar</span>
              <strong>{formatCompactRaw(bsOr(fin.cxpVes, fin.cxpUsd))}</strong>
              <small>Compras {formatCompactRaw(bsOr(fin.comprasMesVes, fin.comprasMesUsd))}</small>
            </Link>
            <Link to="/banca" className="summary-tile summary-tile--link">
              <span>Bancos (Bs.)</span>
              <strong>{formatCompactRaw(bsOr(fin.bancosVes, fin.bancosUsd))}</strong>
              <small>Tesorería convertida a bolívares</small>
            </Link>
            <Link to="/contabilidad" className="summary-tile summary-tile--link summary-tile--accent">
              <span>Utilidad bruta</span>
              <strong>{formatCompactRaw(bsOr(fin.utilidadBrutaVes, fin.utilidadBrutaUsd))}</strong>
              <small>Ingresos {formatCompactRaw(bsOr(fin.ingresosMesVes, fin.ingresosMesUsd))}</small>
            </Link>
            <Link to="/inventario" className="summary-tile summary-tile--link summary-tile--warning">
              <span>Bajo stock</span>
              <strong>{formatInteger(fin.bajoStock)}</strong>
              <small>Productos por reponer</small>
            </Link>
          </div>
        </section>
      ) : null}

      <TransactionsTable />
    </div>
  );
}
